import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, API_BASE, uploadFile } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { PRIMARY, TEXT_MUTED, BORDER_LIGHT, BG_PAGE, BG_CARD, BG_SURFACE, TEXT_DARK, FONT_HEADING } from '../components/legacy-ball/table-management/designTokens';
import { LinkSyncTab } from '../components/kloc/LinkSyncTab';
import { BetaTestingPanel } from '../components/kloc/BetaTestingPanel';

// ─── Types ───────────────────────────────────────────────────────────────────

type SubView = 'events' | 'vendors' | 'performers' | 'provisioning' | 'sponsors' | 'transportation' | 'hotel' | 'app-support' | 'link-sync' | 'beta-testing';

const SUB_VIEWS: { key: SubView; label: string }[] = [
  { key: 'events', label: 'Events' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'performers', label: 'Performers' },
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'sponsors', label: 'Sponsors' },
  { key: 'transportation', label: 'Transportation' },
  { key: 'hotel', label: 'Hotel' },
  { key: 'app-support', label: 'App Support' },
  { key: 'link-sync', label: 'Link & Sync' },
  { key: 'beta-testing', label: '🧪 Beta Testing' },
];

interface EventMetrics {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venueName: string;
  maxCapacity: number;
  registrations: number;
  rsvpGoing: number;
  checkIns: number;
  readinessPercent: number;
  gateEntryCount: number | null;
  hasActiveSession: boolean;
  isLegacyBall: boolean;
  specialMessage: string | null;
  programUrl: string | null;
}

interface VendorEntry {
  id: string;
  businessName: string;
  contactPerson: string;
  phone: string | null;
  category: string | null;
  boothNumber: string | null;
  zone: string | null;
}

interface PerformerEntry {
  id: string;
  title: string;
  speaker: string | null;
  eventName: string;
  date: string;
  startTime: string;
  endTime: string;
}

interface ProvisioningData {
  totalExpectedMeals: number;
  mealBreakdown: { optionName: string; count: number }[];
  dietaryPreferences: { preference: string; count: number }[];
  mealsByTable: { tableNumber: number; tableName: string; zoneName: string; guests: number; vipCount: number; patronCount: number; accessibilityCount: number; meals: { meal: string; count: number }[] }[];
  totalTables: number;
  totalSeatingCapacity: number;
  videoEquipmentNotes: string | null;
  audioEquipmentNotes: string | null;
}

interface SponsorEntry {
  id: string;
  sponsorName: string;
  contactPerson: string;
  phone: string | null;
  tier: string;
  pledgedAmount: number;
  logoUrl: string | null;
}

interface TransportationData {
  venues: { name: string; address: string; city: string; state: string }[];
  parkingLots: { name: string; capacity: string; directions: string }[];
  shuttleInfo: string | null;
}

interface HotelEntry {
  name: string;
  address: string;
  phone: string;
  checkIn: string;
  checkOut: string;
  groupRate: string | null;
  bookingCode: string | null;
}

interface AppSupportData {
  troubleshooting: { issue: string; steps: string }[];
  supportContact: string;
  loginFlowSummary: string;
  chatbotInfo: string;
}

interface EventProvisioningVendor {
  businessName: string;
  category: string | null;
}

interface EventProvisioningPerformer {
  title: string;
  speaker: string | null;
}

interface EventProvisioningEntry {
  id: string;
  name: string;
  date: string;
  venueName: string;
  registeredCount: number;
  rsvpCount: number;
  maxCapacity: number;
  mealInfo: string | null;
  transportInfo: string | null;
  vendors: EventProvisioningVendor[];
  performers: EventProvisioningPerformer[];
}

interface LogisticsAggregateDto {
  summary: {
    totalVendors: number;
    totalPerformers: number;
    totalSponsors: number;
    totalExpectedAttendees: number;
  };
  events: EventMetrics[];
  vendors: VendorEntry[];
  performers: PerformerEntry[];
  provisioning: ProvisioningData;
  sponsors: SponsorEntry[];
  transportation: TransportationData;
  hotel: HotelEntry[];
  appSupport: AppSupportData;
  eventProvisioning: EventProvisioningEntry[];
}

// ─── Utility Functions ───────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  if (timeStr.includes('T')) {
    const [h, m] = timeStr.split('T')[1].slice(0, 5).split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  }
  if (timeStr.includes(':')) {
    const [h, m] = timeStr.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  }
  return timeStr;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  title: {
    fontFamily: FONT_HEADING,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  lastUpdated: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
  },
  refreshBtn: {
    padding: '6px 14px',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    minHeight: '44px',
    minWidth: '44px',
  },
  summaryBanner: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1.25rem',
  },
  summaryCard: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1rem',
    textAlign: 'center' as const,
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: PRIMARY,
    margin: 0,
  },
  summaryLabel: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    marginTop: '4px',
  },
  subNav: {
    display: 'flex',
    gap: '0',
    overflowX: 'auto' as const,
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    marginBottom: '1.25rem',
  },
  subTab: {
    padding: '10px 16px',
    border: 'none',
    background: 'none',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: TEXT_MUTED,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    minHeight: '44px',
    minWidth: '44px',
  },
  subTabActive: {
    padding: '10px 16px',
    border: 'none',
    background: 'none',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: PRIMARY,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    borderBottom: `2px solid ${PRIMARY}`,
    marginBottom: '-2px',
    minHeight: '44px',
    minWidth: '44px',
  },
  card: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '0.75rem',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '0.75rem',
  },
  sectionHeader: {
    fontSize: '1rem',
    fontWeight: 700,
    color: TEXT_DARK,
    marginBottom: '0.75rem',
  },
  groupHeader: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: PRIMARY,
    margin: '1rem 0 0.5rem 0',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    paddingBottom: '4px',
  },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.8rem',
    padding: '2px 0',
    color: TEXT_MUTED,
  },
  spinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem',
    fontSize: '1rem',
    color: TEXT_MUTED,
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem',
    gap: '1rem',
    textAlign: 'center' as const,
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '0.9rem',
  },
  retryBtn: {
    padding: '10px 20px',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 600,
    minHeight: '44px',
    minWidth: '44px',
  },
  readinessBar: {
    height: '6px',
    borderRadius: '3px',
    backgroundColor: BORDER_LIGHT,
    marginTop: '6px',
    overflow: 'hidden',
  },
  readinessFill: {
    height: '100%',
    borderRadius: '3px',
    backgroundColor: PRIMARY,
    transition: 'width 0.3s',
  },
  emptyNotice: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
};

// ── Detailed Counts Types ────────────────────────────────────────────────────

interface DetailedEntranceCounts {
  entranceId: string;
  entranceName: string;
  count: number;
  collectors: { userId: string; userName: string; count: number }[];
}

interface DetailedEntryCountsDto {
  eventId: string;
  totalCount: number;
  byEntrance: DetailedEntranceCounts[];
}

// ─── Event Program Section ───────────────────────────────────────────────────

function EventProgramSection({ event, isOrganizer }: { event: EventMetrics; isOrganizer: boolean }) {
  const [programUrl, setProgramUrl] = useState<string | null>(event.programUrl);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [error, setError] = useState('');

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const data = await uploadFile<{ programUrl: string }>(`/events/${event.id}/program-upload`, file);
      setProgramUrl(data.programUrl);
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSetUrl = async () => {
    if (!urlInput.trim()) return;
    setError('');
    try {
      const data = await apiClient<{ programUrl: string | null }>(`/events/${event.id}/program-url`, {
        method: 'PATCH',
        body: { programUrl: urlInput.trim() },
      });
      setProgramUrl(data.programUrl);
      setUrlInput('');
      setShowUrlInput(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    }
  };

  const handleRemove = async () => {
    setError('');
    try {
      await apiClient(`/events/${event.id}/program`, { method: 'DELETE' });
      setProgramUrl(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to remove');
    }
  };

  return (
    <div style={{ marginTop: '10px', borderTop: `1px solid ${BORDER_LIGHT}`, paddingTop: '8px' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_DARK }}>📄 Event Program</span>
        {programUrl && (
          <a
            href={programUrl.startsWith('/') ? `${API_BASE}${programUrl}` : programUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '0.75rem', color: PRIMARY, textDecoration: 'underline' }}
          >
            View Program
          </a>
        )}
      </div>

      {programUrl ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: TEXT_MUTED }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
            {programUrl.split('/').pop()}
          </span>
          {isOrganizer && (
            <button onClick={handleRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#d32f2f', padding: '2px 4px' }} title="Remove program" aria-label="Remove program">
              🗑️
            </button>
          )}
        </div>
      ) : isOrganizer ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '0.78rem', color: PRIMARY, cursor: 'pointer', fontWeight: 500 }}>
              {uploading ? 'Uploading...' : '📎 Upload File'}
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleFileUpload} style={{ display: 'none' }} disabled={uploading} />
            </label>
            <span style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>or</span>
            <button
              onClick={() => setShowUrlInput(!showUrlInput)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: PRIMARY, padding: 0, fontWeight: 500 }}
            >
              🔗 Paste URL
            </button>
          </div>
          {showUrlInput && (
            <div style={{ display: 'flex', gap: '4px' }}>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://..."
                style={{ flex: 1, padding: '4px 8px', fontSize: '0.78rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px' }}
              />
              <button onClick={handleSetUrl} style={{ padding: '4px 8px', fontSize: '0.75rem', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Save
              </button>
            </div>
          )}
        </div>
      ) : (
        <span style={{ fontSize: '0.78rem', color: TEXT_MUTED, fontStyle: 'italic' }}>No program attached</span>
      )}
      {error && <div style={{ fontSize: '0.72rem', color: '#d32f2f', marginTop: '4px' }}>{error}</div>}
    </div>
  );
}

// ─── Section Components ──────────────────────────────────────────────────────

function EventCard({ event, onRefresh, isMobile }: { event: EventMetrics; onRefresh: () => void; isMobile?: boolean }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [detailedCounts, setDetailedCounts] = useState<DetailedEntryCountsDto | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [editingMessage, setEditingMessage] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [messageSaving, setMessageSaving] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [localMessage, setLocalMessage] = useState<string | null>(event.specialMessage);

  const isOrganizer = user?.role ? isAtLeast(user.role, 'Organizer') : false;

  const handleClick = () => {
    navigate(`/logistics-center/event/${event.id}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't navigate if user is typing in an input/textarea
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Toggle expanded details
  const handleToggleExpand = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    setLoadingDetails(true);
    try {
      const data = await apiClient<DetailedEntryCountsDto>(`/gate/events/${event.id}/counts/detailed`);
      setDetailedCounts(data);
    } catch {
      // silently fail
    } finally {
      setLoadingDetails(false);
    }
  };

  // Session start/stop
  const handleStartSession = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSessionLoading(true);
    try {
      await apiClient(`/gate/events/${event.id}/sessions/start`, { method: 'POST' });
      onRefresh();
    } catch {
      // silently fail
    } finally {
      setSessionLoading(false);
    }
  };

  const handleStopSession = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSessionLoading(true);
    try {
      await apiClient(`/gate/events/${event.id}/sessions/stop`, { method: 'POST' });
      onRefresh();
    } catch {
      // silently fail
    } finally {
      setSessionLoading(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    ...styles.card,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    backgroundColor: hovered ? BG_SURFACE : BG_CARD,
    ...(isMobile ? { padding: '16px', marginBottom: '12px', position: 'relative' as const, minHeight: '72px' } : {}),
  };

  // Gate entry count display logic
  const renderGateCount = () => {
    if (event.gateEntryCount === null) return null;

    const badge = (
      <button
        onClick={handleToggleExpand}
        style={{
          background: 'none',
          border: `1px solid ${BORDER_LIGHT}`,
          borderRadius: '12px',
          padding: '2px 8px',
          fontSize: '0.78rem',
          color: TEXT_DARK,
          cursor: 'pointer',
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
        aria-label={`Gate entries: ${event.gateEntryCount}. Click to ${expanded ? 'collapse' : 'expand'} details`}
        title={expanded ? 'Collapse gate details' : 'Expand gate details'}
      >
        🚪 {event.gateEntryCount} entered
      </button>
    );

    return badge;
  };

  return (
    <div
      key={event.id}
      style={cardStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      tabIndex={0}
      role="button"
      aria-label={`View logistics details for ${event.name}`}
    >
      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: TEXT_DARK, marginBottom: '4px' }}>{event.name}</div>
      <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginBottom: '8px' }}>
        {formatDate(event.date)} • {formatTime(event.startTime)} – {formatTime(event.endTime)}
      </div>
      <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginBottom: '8px' }}>📍 {event.venueName || 'TBD'}</div>
      {isMobile && (
        <span style={{
          position: 'absolute',
          right: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '1.2rem',
          color: TEXT_MUTED,
        }} aria-hidden="true">›</span>
      )}
      <div style={styles.metricRow}><span>Capacity</span><span>{event.maxCapacity}</span></div>
      <div style={styles.metricRow}><span>Registrations</span><span>{event.registrations}</span></div>
      <div style={styles.metricRow}><span>RSVPs (Going)</span><span>{event.rsvpGoing}</span></div>

      {/* Check-in metric: display logic depends on Legacy Ball status */}
      {event.isLegacyBall ? (
        <>
          <div style={styles.metricRow}><span>Check-Ins</span><span>{event.checkIns}</span></div>
          {event.gateEntryCount !== null && (
            <div style={{ ...styles.metricRow, alignItems: 'center' }}>
              <span>Gate Entries</span>
              <span>{renderGateCount()}</span>
            </div>
          )}
        </>
      ) : (
        <>
          {event.gateEntryCount !== null ? (
            <div style={{ ...styles.metricRow, alignItems: 'center' }}>
              <span>Entries</span>
              <span>{renderGateCount()}</span>
            </div>
          ) : (
            <div style={styles.metricRow}><span>Check-Ins</span><span>{event.checkIns}</span></div>
          )}
        </>
      )}

      <div style={styles.metricRow}><span>Readiness</span><span>{event.readinessPercent}%</span></div>
      <div style={styles.readinessBar}>
        <div style={{ ...styles.readinessFill, width: `${Math.min(event.readinessPercent, 100)}%` }} />
      </div>

      {/* Special Message display and inline edit */}
      <div style={{ marginTop: '10px', borderTop: `1px solid ${BORDER_LIGHT}`, paddingTop: '8px' }} onClick={(e) => e.stopPropagation()}>
        {editingMessage ? (
          <div>
            {/* Rich text toolbar */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => document.execCommand('bold')}
                style={{ background: 'none', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '3px', padding: '2px 6px', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem' }}
                title="Bold"
                aria-label="Bold"
              >B</button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => document.execCommand('italic')}
                style={{ background: 'none', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '3px', padding: '2px 6px', cursor: 'pointer', fontStyle: 'italic', fontSize: '0.8rem' }}
                title="Italic"
                aria-label="Italic"
              >I</button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => document.execCommand('insertUnorderedList')}
                style={{ background: 'none', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '3px', padding: '2px 6px', cursor: 'pointer', fontSize: '0.8rem' }}
                title="Bullet List"
                aria-label="Bullet list"
              >• List</button>
            </div>
            <div
              ref={(el) => {
                if (el && !el.getAttribute('data-initialized')) {
                  el.innerHTML = messageDraft;
                  el.setAttribute('data-initialized', 'true');
                }
              }}
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => setMessageDraft((e.target as HTMLDivElement).innerHTML)}
              style={{
                width: '100%',
                minHeight: '80px',
                maxHeight: '200px',
                overflowY: 'auto' as const,
                fontSize: '0.82rem',
                padding: '8px',
                borderRadius: '4px',
                border: `1px solid ${messageDraft.length > 1500 ? '#d32f2f' : BORDER_LIGHT}`,
                fontFamily: 'inherit',
                lineHeight: 1.5,
                outline: 'none',
              }}
              aria-label="Edit special message"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
              <span style={{ fontSize: '0.72rem', color: messageDraft.length > 1500 ? '#d32f2f' : TEXT_MUTED }}>
                {messageDraft.replace(/<[^>]*>/g, '').length}/1500 characters
              </span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => { setEditingMessage(false); setMessageError(null); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: TEXT_MUTED, padding: '4px 8px' }}
                  aria-label="Cancel editing special message"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setMessageSaving(true);
                    setMessageError(null);
                    try {
                      const trimmed = messageDraft.trim();
                      await apiClient(`/events/${event.id}/special-message`, {
                        method: 'PATCH',
                        body: { specialMessage: trimmed || null },
                      });
                      setLocalMessage(trimmed || null);
                      setEditingMessage(false);
                    } catch {
                      setMessageError('Failed to save message');
                    } finally {
                      setMessageSaving(false);
                    }
                  }}
                  disabled={messageDraft.length > 1500 || messageSaving}
                  style={{
                    padding: '4px 10px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    backgroundColor: messageDraft.length > 1500 ? '#ccc' : PRIMARY,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: messageDraft.length > 1500 || messageSaving ? 'not-allowed' : 'pointer',
                    opacity: messageSaving ? 0.6 : 1,
                  }}
                  aria-label="Save special message"
                >
                  {messageSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
            {messageError && (
              <div style={{ fontSize: '0.72rem', color: '#d32f2f', marginTop: '4px' }}>{messageError}</div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
            <div style={{ flex: 1, fontSize: '0.8rem', color: localMessage ? TEXT_DARK : TEXT_MUTED, fontStyle: localMessage ? 'normal' : 'italic' }}>
              {localMessage ? <><span>📢 </span><span dangerouslySetInnerHTML={{ __html: localMessage }} /></> : '📢 No special message'}
            </div>
            {isOrganizer && (
              <button
                onClick={() => { setMessageDraft(localMessage || ''); setEditingMessage(true); setMessageError(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.85rem', borderRadius: '4px', lineHeight: 1 }}
                title="Edit special message"
                aria-label={`Edit special message for ${event.name}`}
              >
                ✏️
              </button>
            )}
          </div>
        )}
      </div>

      {/* Event Program (PDF/Image attachment) */}
      <EventProgramSection event={event} isOrganizer={isOrganizer} />

      {/* Session start/stop actions (Organizer+ only) */}
      {isOrganizer && (
        <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
          {event.hasActiveSession ? (
            <button
              onClick={handleStopSession}
              disabled={sessionLoading}
              style={{
                padding: '6px 12px',
                fontSize: '0.78rem',
                fontWeight: 600,
                backgroundColor: '#d32f2f',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: sessionLoading ? 'not-allowed' : 'pointer',
                opacity: sessionLoading ? 0.6 : 1,
                minHeight: '32px',
              }}
              aria-label={`Stop session for ${event.name}`}
            >
              ⏹ Stop
            </button>
          ) : (
            <button
              onClick={handleStartSession}
              disabled={sessionLoading}
              style={{
                padding: '6px 12px',
                fontSize: '0.78rem',
                fontWeight: 600,
                backgroundColor: PRIMARY,
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: sessionLoading ? 'not-allowed' : 'pointer',
                opacity: sessionLoading ? 0.6 : 1,
                minHeight: '32px',
              }}
              aria-label={`Start session for ${event.name}`}
            >
              ▶ Start
            </button>
          )}
        </div>
      )}

      {/* Expandable entry details */}
      {expanded && (
        <div
          style={{
            marginTop: '10px',
            padding: '10px',
            backgroundColor: BG_SURFACE,
            borderRadius: '6px',
            border: `1px solid ${BORDER_LIGHT}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {loadingDetails ? (
            <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, margin: 0 }}>Loading details...</p>
          ) : detailedCounts && detailedCounts.byEntrance.length > 0 ? (
            <div>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '6px' }}>
                Per-Entrance Breakdown
              </div>
              {detailedCounts.byEntrance.map((entrance) => (
                <div key={entrance.entranceId} style={{ marginBottom: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: TEXT_DARK }}>
                    <span style={{ fontWeight: 500 }}>{entrance.entranceName}</span>
                    <span style={{ fontWeight: 600 }}>{entrance.count}</span>
                  </div>
                  {entrance.collectors.length > 0 && (
                    <div style={{ paddingLeft: '8px', marginTop: '2px' }}>
                      {entrance.collectors.map((c) => (
                        <div key={c.userId} style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>
                          {c.userName}: {c.count}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, margin: 0 }}>No entrance data available.</p>
          )}
        </div>
      )}
    </div>
  );
}

function EventsSection({ events, onRefresh, isMobile }: { events: EventMetrics[]; onRefresh: () => void; isMobile?: boolean }) {
  if (!events || events.length === 0) {
    return <div style={styles.emptyNotice}>No events data available.</div>;
  }
  const eventsSectionStyle: React.CSSProperties = isMobile
    ? { display: 'flex', flexDirection: 'column' }
    : styles.cardGrid;
  return (
    <div>
      <div style={styles.sectionHeader}>All Weekend Events ({events.length})</div>
      <div style={eventsSectionStyle}>
        {events.map((ev) => (
          <EventCard key={ev.id} event={ev} onRefresh={onRefresh} isMobile={isMobile} />
        ))}
      </div>
    </div>
  );
}

function VendorsSection({ vendors }: { vendors: VendorEntry[] }) {
  if (!vendors || vendors.length === 0) {
    return <div style={styles.emptyNotice}>No confirmed vendors.</div>;
  }
  const grouped: Record<string, VendorEntry[]> = {};
  vendors.forEach((v) => {
    const cat = v.category || 'Uncategorized';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(v);
  });
  const categories = Object.keys(grouped).sort();

  return (
    <div>
      <div style={styles.sectionHeader}>Confirmed Vendors ({vendors.length})</div>
      {categories.map((cat) => (
        <div key={cat}>
          <div style={styles.groupHeader}>{cat} ({grouped[cat].length})</div>
          {grouped[cat].map((v) => (
            <div key={v.id} style={styles.card}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: TEXT_DARK }}>{v.businessName}</div>
              <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '4px' }}>
                👤 {v.contactPerson} {v.phone ? `• 📞 ${v.phone}` : ''}
              </div>
              <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '2px' }}>
                Booth: {v.boothNumber || 'Unassigned'} {v.zone ? `• Zone: ${v.zone}` : ''}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function PerformersSection({ performers }: { performers: PerformerEntry[] }) {
  if (!performers || performers.length === 0) {
    return <div style={styles.emptyNotice}>No performer schedule available.</div>;
  }
  return (
    <div>
      <div style={styles.sectionHeader}>Performer Schedule ({performers.length})</div>
      {performers.map((p) => (
        <div key={p.id} style={styles.card}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: TEXT_DARK }}>{p.title}</div>
          {p.speaker && <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '2px' }}>🎤 {p.speaker}</div>}
          <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '4px' }}>
            {p.eventName} • {formatDate(p.date)} • {formatTime(p.startTime)} – {formatTime(p.endTime)}
          </div>
        </div>
      ))}
    </div>
  );
}

function EventProvisioningCard({ entry }: { entry: EventProvisioningEntry }) {
  const hasLogistics = entry.mealInfo || entry.transportInfo ||
    entry.vendors.length > 0 || entry.performers.length > 0;

  const cardStyle: React.CSSProperties = {
    borderRadius: '8px',
    padding: '16px',
    border: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_CARD,
  };

  const headingStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: TEXT_DARK,
    margin: 0,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    marginTop: '4px',
    marginBottom: '10px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: PRIMARY,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    display: 'block',
    marginTop: '10px',
    marginBottom: '4px',
  };

  const listItemStyle: React.CSSProperties = {
    fontSize: '0.8rem',
    color: TEXT_DARK,
    padding: '2px 0',
  };

  const emptyNoteStyle: React.CSSProperties = {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
    marginTop: '10px',
  };

  return (
    <div style={cardStyle}>
      <h3 style={headingStyle}>{entry.name}</h3>
      <div style={subtitleStyle}>
        {formatDate(entry.date)} • {entry.venueName}
      </div>

      {/* Headcount Summary */}
      <div style={styles.metricRow}><span>Registered</span><span>{entry.registeredCount}</span></div>
      <div style={styles.metricRow}><span>RSVP</span><span>{entry.rsvpCount}</span></div>
      <div style={styles.metricRow}><span>Capacity</span><span>{entry.maxCapacity}</span></div>

      {/* Conditional logistics sections */}
      {entry.mealInfo && (
        <div>
          <span style={labelStyle}>Meal Info</span>
          <p style={{ fontSize: '0.8rem', color: TEXT_DARK, margin: 0 }}>{entry.mealInfo}</p>
        </div>
      )}
      {entry.transportInfo && (
        <div>
          <span style={labelStyle}>Transport Info</span>
          <p style={{ fontSize: '0.8rem', color: TEXT_DARK, margin: 0 }}>{entry.transportInfo}</p>
        </div>
      )}
      {entry.vendors.length > 0 && (
        <div>
          <span style={labelStyle}>Vendors</span>
          {entry.vendors.map((v, i) => (
            <div key={i} style={listItemStyle}>
              {v.businessName}{v.category ? ` (${v.category})` : ''}
            </div>
          ))}
        </div>
      )}
      {entry.performers.length > 0 && (
        <div>
          <span style={labelStyle}>Performers</span>
          {entry.performers.map((p, i) => (
            <div key={i} style={listItemStyle}>
              {p.title}{p.speaker ? ` — ${p.speaker}` : ''}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!hasLogistics && (
        <div style={emptyNoteStyle}>No logistics details configured.</div>
      )}
    </div>
  );
}

// ─── Provisioning Detail Modal ───────────────────────────────────────────────

function ProvisioningDetailModal({ title, onClose, onPrint, children }: { title: string; onClose: () => void; onPrint: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }} onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div style={{ backgroundColor: BG_CARD, borderRadius: '12px', maxWidth: '800px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${BORDER_LIGHT}` }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: TEXT_DARK }}>{title}</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={onPrint} style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} aria-label={`Print ${title}`}>🖨️ Print</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '4px 8px', color: TEXT_MUTED, borderRadius: '4px', lineHeight: 1 }} aria-label="Close modal">✕</button>
          </div>
        </div>
        {/* Body */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function ProvisioningSection({ provisioning, eventProvisioning, isMobile }: { provisioning: ProvisioningData | null; eventProvisioning: EventProvisioningEntry[]; isMobile?: boolean }) {
  const [showMealsModal, setShowMealsModal] = useState(false);
  const [showTablesModal, setShowTablesModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);

  if (!provisioning) {
    return <div style={styles.emptyNotice}>Provisioning data not available.</div>;
  }

  const printMealsByTable = () => {
    const rows = (provisioning.mealsByTable || []).map(t => {
      const mealList = t.meals.filter(m => m.meal !== 'Not specified').map(m => `${m.meal}: ${m.count}`).join(', ');
      return `<tr><td>${t.tableNumber}</td><td>${t.tableName}</td><td>${t.zoneName || ''}</td><td>${t.guests}</td><td>${mealList || '—'}</td></tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><title>Meals By Table</title><style>body{font-family:sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px;}td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:12px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}</style></head><body><h1>Meals By Table — Provisioning Report</h1><p>${provisioning.totalExpectedMeals} expected meals • Printed ${new Date().toLocaleDateString()}</p><table><thead><tr><th>#</th><th>Table</th><th>Zone</th><th>Guests</th><th>Meal Selections</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  const printTablesSeating = () => {
    const tablesWithGuests = (provisioning.mealsByTable || []).filter(t => t.guests > 0).sort((a, b) => (a.zoneName || '').localeCompare(b.zoneName || '') || (a.tableName || '').localeCompare(b.tableName || ''));
    const totalGuests = tablesWithGuests.reduce((sum, t) => sum + t.guests, 0);
    const rows = tablesWithGuests.map(t => `<tr><td>${t.tableNumber}</td><td>${t.tableName}</td><td>${t.zoneName || 'Unassigned'}</td><td>${t.guests}</td><td>${t.vipCount}</td><td>${t.patronCount}</td><td>${t.accessibilityCount}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Tables & Seating</title><style>body{font-family:sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px;}td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:12px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}</style></head><body><h1>Tables & Seating — Provisioning Report</h1><p>${provisioning.totalTables} tables, ${provisioning.totalSeatingCapacity} capacity, ${totalGuests} guests assigned • Printed ${new Date().toLocaleDateString()}</p><table><thead><tr><th>#</th><th>Table</th><th>Zone</th><th>Guests</th><th>VIP</th><th>Patrons</th><th>Accessibility</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  const printVideoEquipment = () => {
    const html = `<!DOCTYPE html><html><head><title>Video Equipment</title><style>body{font-family:sans-serif;padding:20px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}.content{font-size:14px;line-height:1.6;white-space:pre-wrap;}</style></head><body><h1>Video Equipment — Provisioning Notes</h1><p>Printed ${new Date().toLocaleDateString()}</p><div class="content">${provisioning.videoEquipmentNotes || 'No notes configured'}</div></body></html>`;
    const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  const printAudioEquipment = () => {
    const html = `<!DOCTYPE html><html><head><title>Audio Equipment</title><style>body{font-family:sans-serif;padding:20px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}.content{font-size:14px;line-height:1.6;white-space:pre-wrap;}</style></head><body><h1>Audio Equipment — Provisioning Notes</h1><p>Printed ${new Date().toLocaleDateString()}</p><div class="content">${provisioning.audioEquipmentNotes || 'No notes configured'}</div></body></html>`;
    const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  const clickableTitleStyle: React.CSSProperties = { fontWeight: 600, fontSize: '0.9rem', color: PRIMARY, cursor: 'pointer', background: 'none', border: 'none', padding: 0, textDecoration: 'underline', textDecorationColor: 'transparent', transition: 'text-decoration-color 0.2s' };

  const sectionGridStyle: React.CSSProperties = isMobile
    ? { display: 'flex', flexDirection: 'column', gap: '8px' }
    : styles.cardGrid;
  return (
    <div>
      {/* Meals By Table Detail Modal */}
      {showMealsModal && <ProvisioningDetailModal title="🍽️ Meals By Table" onClose={() => setShowMealsModal(false)} onPrint={printMealsByTable}>
        <div style={{ marginBottom: '12px' }}>
          <div style={styles.metricRow}><span style={{ fontWeight: 600 }}>Total Expected Meals</span><span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{provisioning.totalExpectedMeals}</span></div>
        </div>
        {provisioning.mealBreakdown.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_MUTED, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Formal Selections</div>
            {provisioning.mealBreakdown.map(m => <div key={m.optionName} style={styles.metricRow}><span>{m.optionName}</span><span>{m.count}</span></div>)}
          </div>
        )}
        {provisioning.dietaryPreferences && provisioning.dietaryPreferences.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_MUTED, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Dietary Preferences (From Guest Records)</div>
            {provisioning.dietaryPreferences.map(d => <div key={d.preference} style={styles.metricRow}><span>{d.preference}</span><span>{d.count}</span></div>)}
            <div style={styles.metricRow}><span style={{ fontStyle: 'italic', color: TEXT_MUTED }}>No preference stated</span><span>{provisioning.totalExpectedMeals - provisioning.dietaryPreferences.reduce((sum, d) => sum + d.count, 0)}</span></div>
          </div>
        )}
        {provisioning.mealsByTable && provisioning.mealsByTable.length > 0 && (() => {
          const allMeals = new Set<string>();
          for (const t of provisioning.mealsByTable) { for (const m of t.meals) { if (m.meal !== 'Not specified') allMeals.add(m.meal); } }
          const mealColumns = Array.from(allMeals).sort();
          return (
            <div style={{ borderTop: `1px solid ${BORDER_LIGHT}`, paddingTop: '12px' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_MUTED, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Breakdown By Table</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead><tr>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: TEXT_DARK, borderBottom: `2px solid ${BORDER_LIGHT}` }}>Table</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: TEXT_DARK, borderBottom: `2px solid ${BORDER_LIGHT}` }}>Zone</th>
                    {mealColumns.map(col => <th key={col} style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, color: TEXT_DARK, borderBottom: `2px solid ${BORDER_LIGHT}` }}>{col}</th>)}
                    <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: TEXT_DARK, borderBottom: `2px solid ${BORDER_LIGHT}` }}>Total</th>
                  </tr></thead>
                  <tbody>
                    {provisioning.mealsByTable.map((t, idx) => {
                      const mealMap = new Map(t.meals.map(m => [m.meal, m.count]));
                      return (<tr key={t.tableNumber} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : BG_SURFACE }}>
                        <td style={{ padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}` }}><span style={{ fontWeight: 600 }}>#{t.tableNumber}</span> {t.tableName}</td>
                        <td style={{ padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: TEXT_MUTED }}>{t.zoneName || '—'}</td>
                        {mealColumns.map(col => <td key={col} style={{ textAlign: 'center', padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: mealMap.get(col) ? TEXT_DARK : TEXT_MUTED }}>{mealMap.get(col) || 0}</td>)}
                        <td style={{ textAlign: 'center', padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}`, fontWeight: 700 }}>{t.guests}</td>
                      </tr>);
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </ProvisioningDetailModal>}

      {/* Tables & Seating Detail Modal */}
      {showTablesModal && <ProvisioningDetailModal title="🪑 Tables & Seating" onClose={() => setShowTablesModal(false)} onPrint={printTablesSeating}>
        <div style={{ marginBottom: '12px' }}>
          <div style={styles.metricRow}><span style={{ fontWeight: 600 }}>Total Tables</span><span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{provisioning.totalTables}</span></div>
          <div style={styles.metricRow}><span style={{ fontWeight: 600 }}>Seating Capacity</span><span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{provisioning.totalSeatingCapacity}</span></div>
        </div>
        {provisioning.mealsByTable && provisioning.mealsByTable.filter(t => t.guests > 0).length > 0 && (() => {
          const tablesWithGuests = provisioning.mealsByTable.filter(t => t.guests > 0).sort((a, b) => (a.zoneName || '').localeCompare(b.zoneName || '') || (a.tableName || '').localeCompare(b.tableName || ''));
          const totalGuests = tablesWithGuests.reduce((sum, t) => sum + t.guests, 0);
          const zones = [...new Set(tablesWithGuests.map(t => t.zoneName || 'Unassigned'))];
          return (
            <div style={{ borderTop: `1px solid ${BORDER_LIGHT}`, paddingTop: '12px' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_MUTED, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Occupied Tables — {tablesWithGuests.length} tables, {totalGuests} guests
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead><tr style={{ backgroundColor: BG_SURFACE }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: TEXT_DARK, borderBottom: `2px solid ${BORDER_LIGHT}` }}>Table</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: TEXT_DARK, borderBottom: `2px solid ${BORDER_LIGHT}` }}>Guests</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: '#f9a825', borderBottom: `2px solid ${BORDER_LIGHT}` }}>VIP</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: '#6a1b9a', borderBottom: `2px solid ${BORDER_LIGHT}` }}>Patrons</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: '#1565c0', borderBottom: `2px solid ${BORDER_LIGHT}` }}>Accessibility</th>
                </tr></thead>
                <tbody>
                  {zones.map(zone => {
                    const zoneTables = tablesWithGuests.filter(t => (t.zoneName || 'Unassigned') === zone);
                    const zoneTotal = zoneTables.reduce((sum, t) => sum + t.guests, 0);
                    return (
                      <React.Fragment key={zone}>
                        <tr><td colSpan={5} style={{ padding: '8px 8px 4px', fontWeight: 700, fontSize: '0.78rem', color: PRIMARY, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${BORDER_LIGHT}` }}>{zone} ({zoneTables.length} tables, {zoneTotal} guests)</td></tr>
                        {zoneTables.map((t, idx) => (
                          <tr key={t.tableNumber} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : BG_SURFACE }}>
                            <td style={{ padding: '5px 8px 5px 16px', borderBottom: `1px solid ${BORDER_LIGHT}` }}><span style={{ fontWeight: 500 }}>{t.tableName}</span> <span style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>#{t.tableNumber}</span></td>
                            <td style={{ textAlign: 'center', padding: '5px 8px', fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>{t.guests}</td>
                            <td style={{ textAlign: 'center', padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: t.vipCount > 0 ? '#f9a825' : TEXT_MUTED }}>{t.vipCount}</td>
                            <td style={{ textAlign: 'center', padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: t.patronCount > 0 ? '#6a1b9a' : TEXT_MUTED }}>{t.patronCount}</td>
                            <td style={{ textAlign: 'center', padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: t.accessibilityCount > 0 ? '#1565c0' : TEXT_MUTED }}>{t.accessibilityCount}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  <tr style={{ backgroundColor: BG_SURFACE }}>
                    <td style={{ padding: '6px 8px', fontWeight: 700, borderTop: `2px solid ${BORDER_LIGHT}` }}>Total</td>
                    <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, borderTop: `2px solid ${BORDER_LIGHT}` }}>{totalGuests}</td>
                    <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: '#f9a825', borderTop: `2px solid ${BORDER_LIGHT}` }}>{tablesWithGuests.reduce((s, t) => s + t.vipCount, 0)}</td>
                    <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: '#6a1b9a', borderTop: `2px solid ${BORDER_LIGHT}` }}>{tablesWithGuests.reduce((s, t) => s + t.patronCount, 0)}</td>
                    <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: '#1565c0', borderTop: `2px solid ${BORDER_LIGHT}` }}>{tablesWithGuests.reduce((s, t) => s + t.accessibilityCount, 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}
      </ProvisioningDetailModal>}

      {/* Video Equipment Detail Modal */}
      {showVideoModal && <ProvisioningDetailModal title="🎥 Video Equipment" onClose={() => setShowVideoModal(false)} onPrint={printVideoEquipment}>
        <div style={{ fontSize: '0.9rem', color: TEXT_DARK, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {provisioning.videoEquipmentNotes || 'No video equipment notes have been configured yet.'}
        </div>
      </ProvisioningDetailModal>}

      {/* Audio Equipment Detail Modal */}
      {showAudioModal && <ProvisioningDetailModal title="🔊 Audio Equipment" onClose={() => setShowAudioModal(false)} onPrint={printAudioEquipment}>
        <div style={{ fontSize: '0.9rem', color: TEXT_DARK, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {provisioning.audioEquipmentNotes || 'No audio equipment notes have been configured yet.'}
        </div>
      </ProvisioningDetailModal>}

      <div style={styles.sectionHeader}>Provisioning Summary</div>
      <div style={sectionGridStyle}>
        {/* Meals card with sub-table */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <button style={clickableTitleStyle} onClick={() => setShowMealsModal(true)} onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecorationColor = PRIMARY; }} onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecorationColor = 'transparent'; }} aria-label="View Meals By Table detail">🍽️ Meals By Table</button>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: TEXT_MUTED, padding: '2px 4px' }} onClick={printMealsByTable} aria-label="Print meals by table report">🖨️</button>
          </div>
          <div style={styles.metricRow}><span>Total Expected</span><span>{provisioning.totalExpectedMeals}</span></div>
          {provisioning.mealBreakdown.length > 0 && (
            <>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_MUTED, marginTop: '8px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Formal Selections</div>
              {provisioning.mealBreakdown.map((m) => (
                <div key={m.optionName} style={styles.metricRow}><span>{m.optionName}</span><span>{m.count}</span></div>
              ))}
            </>
          )}
          {provisioning.dietaryPreferences && provisioning.dietaryPreferences.length > 0 ? (
            <>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_MUTED, marginTop: '8px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>From Guest Records</div>
              {provisioning.dietaryPreferences.map((d) => (
                <div key={d.preference} style={styles.metricRow}><span>{d.preference}</span><span>{d.count}</span></div>
              ))}
              <div style={styles.metricRow}>
                <span style={{ fontStyle: 'italic', color: TEXT_MUTED }}>No preference stated</span>
                <span>{provisioning.totalExpectedMeals - provisioning.dietaryPreferences.reduce((sum, d) => sum + d.count, 0)}</span>
              </div>
            </>
          ) : (
            provisioning.mealBreakdown.length === 0 && (
              <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '6px', fontStyle: 'italic' }}>Meal selection data pending</div>
            )
          )}

          {/* Meal breakdown by table — columnar sub-table */}
          {provisioning.mealsByTable && provisioning.mealsByTable.length > 0 && (() => {
            // Derive unique meal option columns from the data
            const allMeals = new Set<string>();
            for (const t of provisioning.mealsByTable) {
              for (const m of t.meals) {
                if (m.meal !== 'Not specified') allMeals.add(m.meal);
              }
            }
            const mealColumns = Array.from(allMeals).sort();

            return (
              <div style={{ marginTop: '12px', borderTop: `1px solid ${BORDER_LIGHT}`, paddingTop: '10px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_MUTED, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>By Table</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600, color: TEXT_DARK, borderBottom: `1px solid ${BORDER_LIGHT}`, whiteSpace: 'nowrap' }}>Table</th>
                        {mealColumns.map((col) => (
                          <th key={col} style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 600, color: TEXT_DARK, borderBottom: `1px solid ${BORDER_LIGHT}`, whiteSpace: 'nowrap' }}>{col.split(' ')[0]}</th>
                        ))}
                        <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 600, color: '#f9a825', borderBottom: `1px solid ${BORDER_LIGHT}`, whiteSpace: 'nowrap' }}>VIP</th>
                        <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 600, color: '#6a1b9a', borderBottom: `1px solid ${BORDER_LIGHT}`, whiteSpace: 'nowrap' }}>Patrons</th>
                        <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 600, color: '#1565c0', borderBottom: `1px solid ${BORDER_LIGHT}`, whiteSpace: 'nowrap' }}>Accessibility</th>
                        <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700, color: TEXT_DARK, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {provisioning.mealsByTable.map((t) => {
                        const mealMap = new Map(t.meals.map((m) => [m.meal, m.count]));
                        return (
                          <tr key={t.tableNumber}>
                            <td style={{ padding: '3px 6px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: TEXT_DARK, whiteSpace: 'nowrap' }}>
                              <span style={{ fontWeight: 600 }}>#{t.tableNumber}</span> {t.tableName}
                            </td>
                            {mealColumns.map((col) => (
                              <td key={col} style={{ textAlign: 'center', padding: '3px 6px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: mealMap.get(col) ? TEXT_DARK : TEXT_MUTED }}>
                                {mealMap.get(col) || 0}
                              </td>
                            ))}
                            <td style={{ textAlign: 'center', padding: '3px 6px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: t.vipCount > 0 ? '#f9a825' : TEXT_MUTED }}>
                              {t.vipCount}
                            </td>
                            <td style={{ textAlign: 'center', padding: '3px 6px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: t.patronCount > 0 ? '#6a1b9a' : TEXT_MUTED }}>
                              {t.patronCount}
                            </td>
                            <td style={{ textAlign: 'center', padding: '3px 6px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: t.accessibilityCount > 0 ? '#1565c0' : TEXT_MUTED }}>
                              {t.accessibilityCount}
                            </td>
                            <td style={{ textAlign: 'center', padding: '3px 6px', borderBottom: `1px solid ${BORDER_LIGHT}`, fontWeight: 700, color: TEXT_DARK }}>
                              {t.guests}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Totals row */}
                      <tr style={{ backgroundColor: BG_SURFACE }}>
                        <td style={{ padding: '4px 6px', fontWeight: 700, color: TEXT_DARK }}>Totals</td>
                        {mealColumns.map((col) => {
                          const colTotal = provisioning.mealsByTable.reduce((sum, t) => {
                            const found = t.meals.find((m) => m.meal === col);
                            return sum + (found ? found.count : 0);
                          }, 0);
                          return (
                            <td key={col} style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700, color: TEXT_DARK }}>{colTotal}</td>
                          );
                        })}
                        <td style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700, color: '#f9a825' }}>
                          {provisioning.mealsByTable.reduce((sum, t) => sum + t.vipCount, 0)}
                        </td>
                        <td style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700, color: '#6a1b9a' }}>
                          {provisioning.mealsByTable.reduce((sum, t) => sum + t.patronCount, 0)}
                        </td>
                        <td style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700, color: '#1565c0' }}>
                          {provisioning.mealsByTable.reduce((sum, t) => sum + t.accessibilityCount, 0)}
                        </td>
                        <td style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700, color: TEXT_DARK }}>
                          {provisioning.mealsByTable.reduce((sum, t) => sum + t.guests, 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Tables card */}
        <div style={styles.card}>
          <button style={{ ...clickableTitleStyle, marginBottom: '8px', display: 'block' }} onClick={() => setShowTablesModal(true)} onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecorationColor = PRIMARY; }} onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecorationColor = 'transparent'; }} aria-label="View Tables & Seating detail">🪑 Tables & Seating</button>
          <div style={styles.metricRow}><span>Total Tables</span><span>{provisioning.totalTables}</span></div>
          <div style={styles.metricRow}><span>Seating Capacity</span><span>{provisioning.totalSeatingCapacity}</span></div>
          {provisioning.mealsByTable && provisioning.mealsByTable.filter(t => t.guests > 0).length > 0 && (() => {
            const tablesWithGuests = provisioning.mealsByTable
              .filter(t => t.guests > 0)
              .sort((a, b) => (a.zoneName || '').localeCompare(b.zoneName || '') || (a.tableName || '').localeCompare(b.tableName || ''));
            const totalGuests = tablesWithGuests.reduce((sum, t) => sum + t.guests, 0);
            // Group by zone
            const zones = [...new Set(tablesWithGuests.map(t => t.zoneName || 'Unassigned'))];

            return (
              <div style={{ marginTop: '12px', borderTop: `1px solid ${BORDER_LIGHT}`, paddingTop: '10px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_MUTED, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Tables with Guests — {tablesWithGuests.length} tables, {totalGuests} guests
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: BG_SURFACE }}>
                      <th style={{ textAlign: 'left', padding: '5px 8px', fontWeight: 700, color: TEXT_DARK, borderBottom: `2px solid ${BORDER_LIGHT}` }}>Table</th>
                      <th style={{ textAlign: 'center', padding: '5px 8px', fontWeight: 700, color: TEXT_DARK, borderBottom: `2px solid ${BORDER_LIGHT}` }}>Guests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map(zone => {
                      const zoneTables = tablesWithGuests.filter(t => (t.zoneName || 'Unassigned') === zone);
                      const zoneTotal = zoneTables.reduce((sum, t) => sum + t.guests, 0);
                      return (
                        <React.Fragment key={zone}>
                          <tr>
                            <td colSpan={2} style={{ padding: '6px 8px 3px', fontWeight: 700, fontSize: '0.72rem', color: PRIMARY, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                              {zone} ({zoneTables.length} tables, {zoneTotal} guests)
                            </td>
                          </tr>
                          {zoneTables.map((t, idx) => (
                            <tr key={t.tableNumber} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : BG_SURFACE }}>
                              <td style={{ padding: '4px 8px 4px 16px', color: TEXT_DARK, borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                                <span style={{ fontWeight: 500 }}>{t.tableName}</span>
                                <span style={{ marginLeft: '6px', fontSize: '0.68rem', color: TEXT_MUTED }}>#{t.tableNumber}</span>
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 8px', fontWeight: 600, color: TEXT_DARK, borderBottom: `1px solid ${BORDER_LIGHT}` }}>{t.guests}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                    <tr style={{ backgroundColor: BG_SURFACE }}>
                      <td style={{ padding: '6px 8px', fontWeight: 700, color: TEXT_DARK, borderTop: `2px solid ${BORDER_LIGHT}` }}>Total</td>
                      <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: TEXT_DARK, borderTop: `2px solid ${BORDER_LIGHT}` }}>{totalGuests}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>

        {/* Equipment cards */}
        <div style={styles.card}>
          <button style={{ ...clickableTitleStyle, marginBottom: '8px', display: 'block' }} onClick={() => setShowVideoModal(true)} onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecorationColor = PRIMARY; }} onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecorationColor = 'transparent'; }} aria-label="View Video Equipment detail">🎥 Video Equipment</button>
          <div style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>
            {provisioning.videoEquipmentNotes || 'No notes configured'}
          </div>
        </div>

        <div style={styles.card}>
          <button style={{ ...clickableTitleStyle, marginBottom: '8px', display: 'block' }} onClick={() => setShowAudioModal(true)} onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecorationColor = PRIMARY; }} onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecorationColor = 'transparent'; }} aria-label="View Audio Equipment detail">🔊 Audio Equipment</button>
          <div style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>
            {provisioning.audioEquipmentNotes || 'No notes configured'}
          </div>
        </div>
      </div>

      {/* All Events Provisioning */}
      <div style={{ ...styles.sectionHeader, marginTop: '1.5rem' }}>All Events Provisioning</div>
      {eventProvisioning.length === 0 ? (
        <div style={styles.emptyNotice}>No additional events are available.</div>
      ) : (
        <div style={isMobile
          ? { display: 'flex', flexDirection: 'column' as const, gap: '12px' }
          : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }
        }>
          {eventProvisioning.map((entry) => (
            <EventProvisioningCard key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

const TIER_ORDER = ['Platinum', 'Gold', 'Silver', 'Bronze', 'Custom'];

function SponsorsSection({ sponsors }: { sponsors: SponsorEntry[] }) {
  if (!sponsors || sponsors.length === 0) {
    return <div style={styles.emptyNotice}>No active sponsors.</div>;
  }
  const grouped: Record<string, SponsorEntry[]> = {};
  sponsors.forEach((s) => {
    const tier = s.tier || 'Custom';
    if (!grouped[tier]) grouped[tier] = [];
    grouped[tier].push(s);
  });

  return (
    <div>
      <div style={styles.sectionHeader}>Active Sponsors ({sponsors.length})</div>
      {TIER_ORDER.filter((t) => grouped[t]?.length).map((tier) => (
        <div key={tier}>
          <div style={styles.groupHeader}>{tier} ({grouped[tier].length})</div>
          {grouped[tier].map((s) => (
            <div key={s.id} style={{ ...styles.card, display: 'flex', alignItems: 'center', gap: '12px' }}>
              {s.logoUrl && <img src={s.logoUrl} alt={s.sponsorName} style={{ width: 40, height: 40, borderRadius: '4px', objectFit: 'cover' }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: TEXT_DARK }}>{s.sponsorName}</div>
                <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '2px' }}>
                  👤 {s.contactPerson} {s.phone ? `• 📞 ${s.phone}` : ''}
                </div>
                <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '2px' }}>
                  💰 ${s.pledgedAmount.toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function TransportationSection({ transportation }: { transportation: TransportationData | null }) {
  if (!transportation) {
    return <div style={styles.emptyNotice}>Transportation data not available.</div>;
  }
  return (
    <div>
      <div style={styles.sectionHeader}>Transportation & Parking</div>

      {transportation.venues.length > 0 && (
        <>
          <div style={styles.groupHeader}>Venue Addresses</div>
          {transportation.venues.map((v, i) => (
            <div key={i} style={styles.card}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: TEXT_DARK }}>{v.name}</div>
              <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '4px' }}>
                📍 {v.address}, {v.city}, {v.state}
              </div>
            </div>
          ))}
        </>
      )}

      {transportation.parkingLots.length > 0 && (
        <>
          <div style={styles.groupHeader}>Parking Lots</div>
          {transportation.parkingLots.map((p, i) => (
            <div key={i} style={styles.card}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: TEXT_DARK }}>{p.name}</div>
              <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '4px' }}>
                Capacity: {p.capacity} • {p.directions}
              </div>
            </div>
          ))}
        </>
      )}

      {transportation.shuttleInfo && (
        <>
          <div style={styles.groupHeader}>Shuttle Service</div>
          <div style={styles.card}>
            <div style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>{transportation.shuttleInfo}</div>
          </div>
        </>
      )}

      {!transportation.venues.length && !transportation.parkingLots.length && !transportation.shuttleInfo && (
        <div style={styles.emptyNotice}>No transportation info configured yet.</div>
      )}
    </div>
  );
}

function HotelSection({ hotels, isMobile }: { hotels: HotelEntry[]; isMobile?: boolean }) {
  if (!hotels || hotels.length === 0) {
    return <div style={styles.emptyNotice}>Hotel details are pending configuration.</div>;
  }
  const sectionGridStyle: React.CSSProperties = isMobile
    ? { display: 'flex', flexDirection: 'column', gap: '8px' }
    : styles.cardGrid;
  return (
    <div>
      <div style={styles.sectionHeader}>Hotel Information</div>
      <div style={sectionGridStyle}>
        {hotels.map((h, i) => (
          <div key={i} style={styles.card}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: TEXT_DARK, marginBottom: '6px' }}>🏨 {h.name}</div>
            <div style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>📍 {h.address}</div>
            <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '2px' }}>📞 {h.phone}</div>
            <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '4px' }}>
              Check-in: {h.checkIn} • Check-out: {h.checkOut}
            </div>
            {h.groupRate && <div style={{ fontSize: '0.8rem', color: PRIMARY, marginTop: '4px', fontWeight: 500 }}>Group Rate: {h.groupRate}</div>}
            {h.bookingCode && <div style={{ fontSize: '0.8rem', color: PRIMARY, marginTop: '2px', fontWeight: 500 }}>Code: {h.bookingCode}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function AppSupportSection({ appSupport, isMobile }: { appSupport: AppSupportData | null; isMobile?: boolean }) {
  if (!appSupport) {
    return <div style={styles.emptyNotice}>App support data not available.</div>;
  }
  const sectionGridStyle: React.CSSProperties = isMobile
    ? { display: 'flex', flexDirection: 'column', gap: '8px' }
    : styles.cardGrid;
  return (
    <div>
      <div style={styles.sectionHeader}>KDU Hub App Support</div>
      <div style={sectionGridStyle}>
        {/* Troubleshooting */}
        <div style={styles.card}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: TEXT_DARK, marginBottom: '8px' }}>🛠️ Troubleshooting</div>
          {appSupport.troubleshooting.length > 0 ? (
            appSupport.troubleshooting.map((t, i) => (
              <div key={i} style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: 600, fontSize: '0.8rem', color: TEXT_DARK }}>{t.issue}</div>
                <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, whiteSpace: 'pre-line' }}>{t.steps}</div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>No troubleshooting steps configured.</div>
          )}
        </div>

        {/* Login Flow */}
        <div style={styles.card}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: TEXT_DARK, marginBottom: '8px' }}>🔐 Guest Login Flow</div>
          <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, whiteSpace: 'pre-line' }}>
            {appSupport.loginFlowSummary || 'Login flow info not configured.'}
          </div>
        </div>

        {/* Chatbot */}
        <div style={styles.card}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: TEXT_DARK, marginBottom: '8px' }}>🤖 AI Concierge Chatbot</div>
          <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, whiteSpace: 'pre-line' }}>
            {appSupport.chatbotInfo || 'Chatbot info not configured.'}
          </div>
        </div>

        {/* Support contact */}
        <div style={styles.card}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: TEXT_DARK, marginBottom: '8px' }}>📞 Support Contact</div>
          <div style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>
            {appSupport.supportContact || 'Support contact not configured.'}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page Component ─────────────────────────────────────────────────────

export function LogisticsOperationsCenterPage() {
  const [activeView, setActiveView] = useState<SubView>('events');
  const [data, setData] = useState<LogisticsAggregateDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const isMobile = useMediaQuery(767);
  const navRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient<LogisticsAggregateDto>('/logistics-center');
      setData({ ...result, eventProvisioning: result.eventProvisioning ?? [] });
      setLastUpdated(new Date());
    } catch (err: unknown) {
      const message = (err && typeof err === 'object' && 'message' in err) ? String((err as { message: string }).message) : 'Failed to load data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-scroll to active tab on mobile
  useEffect(() => {
    if (isMobile && navRef.current) {
      const activeBtn = navRef.current.querySelector('[aria-current="page"]');
      if (activeBtn) {
        (activeBtn as HTMLElement).scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeView, isMobile]);

  // Mobile-conditional styles
  const pageStyle: React.CSSProperties = {
    ...styles.page,
    padding: isMobile ? '8px 12px' : '1.5rem',
  };

  const headerStyle: React.CSSProperties = isMobile
    ? { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '0.75rem' }
    : styles.header;

  const headerRightStyle: React.CSSProperties = isMobile
    ? { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }
    : styles.headerRight;

  const summaryBannerStyle: React.CSSProperties = isMobile
    ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '1rem' }
    : styles.summaryBanner;

  const summaryCardStyle: React.CSSProperties = isMobile
    ? { ...styles.summaryCard, padding: '10px 8px' }
    : styles.summaryCard;

  const summaryValueStyle: React.CSSProperties = isMobile
    ? { ...styles.summaryValue, fontSize: '1.25rem' }
    : styles.summaryValue;

  const summaryLabelStyle: React.CSSProperties = isMobile
    ? { ...styles.summaryLabel, fontSize: '0.7rem' }
    : styles.summaryLabel;

  const subNavStyle: React.CSSProperties = isMobile
    ? {
        ...styles.subNav,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: BG_PAGE,
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        overflowX: 'auto',
        flexWrap: 'nowrap',
      }
    : styles.subNav;

  const subTabStyle = (isActive: boolean): React.CSSProperties => {
    const base = isActive ? styles.subTabActive : styles.subTab;
    if (isMobile) {
      return { ...base, minHeight: '44px', padding: '0 16px' };
    }
    return base;
  };

  // Loading state (no data yet)
  if (loading && !data) {
    return (
      <div style={pageStyle}>
        <div style={styles.spinner}>⏳ Loading K-LOC...</div>
      </div>
    );
  }

  // Error state with no data
  if (error && !data) {
    return (
      <div style={pageStyle}>
        <div style={styles.errorState}>
          <div style={styles.errorText}>⚠️ {error}</div>
          <button style={styles.retryBtn} onClick={fetchData} aria-label="Try again">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* WebKit scrollbar hide for mobile sub-nav */}
      {isMobile && (
        <style>{`.kloc-mobile-subnav::-webkit-scrollbar { display: none; }`}</style>
      )}

      {/* Header */}
      <div style={headerStyle}>
        <h1 style={styles.title}>{isMobile ? 'K-LOC' : 'KDU-HUB Logistics Operations Center (K-LOC)'}</h1>
        <div style={headerRightStyle}>
          {lastUpdated && (
            <span style={styles.lastUpdated}>
              Last Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          {user && isAtLeast(user.role, 'Admin') && (
            <button
              style={{ ...styles.refreshBtn, backgroundColor: '#6b21a8', ...(isMobile ? { minHeight: '44px', minWidth: '44px' } : {}) }}
              onClick={() => navigate('/observability')}
              aria-label="Observability Dashboard"
            >
              📊 {isMobile ? '' : 'Observability'}
            </button>
          )}
          {user && isAtLeast(user.role, 'Organizer') && (
            <button
              style={{ ...styles.refreshBtn, backgroundColor: '#1a6b3c', ...(isMobile ? { minHeight: '44px', minWidth: '44px' } : {}) }}
              onClick={() => navigate('/all-tasks')}
              aria-label="All Event Tasks"
            >
              📝 {isMobile ? '' : 'All Tasks'}
            </button>
          )}
          {user && isAtLeast(user.role, 'Delegate') && (
            <button
              style={{ ...styles.refreshBtn, backgroundColor: '#16a34a', ...(isMobile ? { minHeight: '44px', minWidth: '44px' } : {}) }}
              onClick={() => navigate('/packet-pickup')}
              aria-label="Packet Pickup"
            >
              📦 {isMobile ? '' : 'Packet Pickup'}
            </button>
          )}
          {user && isAtLeast(user.role, 'Delegate') && (
            <button
              style={{ ...styles.refreshBtn, backgroundColor: '#0d47a1', ...(isMobile ? { minHeight: '44px', minWidth: '44px' } : {}) }}
              onClick={() => navigate('/qr-generator')}
              aria-label="QR Guest Cards"
            >
              🎫 {isMobile ? '' : 'QR Cards'}
            </button>
          )}
          {user && isAtLeast(user.role, 'Delegate') && (
            <button
              style={{ ...styles.refreshBtn, backgroundColor: '#7b1fa2', ...(isMobile ? { minHeight: '44px', minWidth: '44px' } : {}) }}
              onClick={() => window.open('/qr-signage', '_blank')}
              aria-label="Event QR Signage"
            >
              📲 {isMobile ? '' : 'Login QR'}
            </button>
          )}
          <button style={{ ...styles.refreshBtn, ...(isMobile ? { minHeight: '44px', minWidth: '44px' } : {}) }} onClick={fetchData} disabled={loading} aria-label="Refresh data">
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>
      </div>

      {/* Error banner (with stale data) */}
      {error && data && (
        <div style={{ backgroundColor: '#fff3e0', border: '1px solid #ff9800', borderRadius: '6px', padding: '10px 14px', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.8rem', color: '#e65100' }}>⚠️ {error}</span>
          <button onClick={fetchData} style={{ background: 'none', border: 'none', color: PRIMARY, fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem', minHeight: '44px', minWidth: '44px' }} aria-label="Retry">Retry</button>
        </div>
      )}

      {/* Summary banner */}
      {data && (
        <div style={summaryBannerStyle}>
          <div style={summaryCardStyle}>
            <div style={summaryValueStyle}>{data.summary.totalVendors}</div>
            <div style={summaryLabelStyle}>Vendors</div>
          </div>
          <div style={summaryCardStyle}>
            <div style={summaryValueStyle}>{data.summary.totalPerformers}</div>
            <div style={summaryLabelStyle}>Performers</div>
          </div>
          <div style={summaryCardStyle}>
            <div style={summaryValueStyle}>{data.summary.totalSponsors}</div>
            <div style={summaryLabelStyle}>Sponsors</div>
          </div>
          <div style={summaryCardStyle}>
            <div style={summaryValueStyle}>{data.summary.totalExpectedAttendees}</div>
            <div style={summaryLabelStyle}>Expected Attendees</div>
          </div>
        </div>
      )}

      {/* Sub-tab navigation */}
      <nav
        ref={navRef}
        style={subNavStyle}
        className={isMobile ? 'kloc-mobile-subnav' : undefined}
        aria-label="Logistics section views"
      >
        {SUB_VIEWS.map((view) => (
          <button
            key={view.key}
            style={subTabStyle(activeView === view.key)}
            onClick={() => setActiveView(view.key)}
            aria-current={activeView === view.key ? 'page' : undefined}
          >
            {view.label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      {data && (
        <>
          {activeView === 'events' && <EventsSection events={data.events} onRefresh={fetchData} isMobile={isMobile} />}
          {activeView === 'vendors' && <VendorsSection vendors={data.vendors} />}
          {activeView === 'performers' && <PerformersSection performers={data.performers} />}
          {activeView === 'provisioning' && <ProvisioningSection provisioning={data.provisioning} eventProvisioning={data.eventProvisioning ?? []} isMobile={isMobile} />}
          {activeView === 'sponsors' && <SponsorsSection sponsors={data.sponsors} />}
          {activeView === 'transportation' && <TransportationSection transportation={data.transportation} />}
          {activeView === 'hotel' && <HotelSection hotels={data.hotel} isMobile={isMobile} />}
          {activeView === 'app-support' && <AppSupportSection appSupport={data.appSupport} isMobile={isMobile} />}
        </>
      )}
      {activeView === 'link-sync' && <LinkSyncTab />}
      {activeView === 'beta-testing' && <BetaTestingPanel />}
    </div>
  );
}
